import React, { useMemo, useEffect } from 'react';
import { Grid2, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { useFormContext } from 'react-hook-form';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import ExpenseCategoryType from './ExpenseCategoryType';
import ExpenseBillType from './ExpenseBillType';
import ExpensePayDate from './ExpensePayDate';
import MiscExpenseDescription from './MiscExpenseDescription';
import ExpenseDescription from './ExpenseDescription';
import VendorPaymentStartDate from './VendorPaymentStartDate';
import VendorPaymentEndDate from './VendorPaymentEndDate';
import { ExpenseClassEnum } from './ExpenseClass.enum';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { ExpenseCategoryEnum } from './ExpenseCategory.enum';
import { ExpenseTypeEnum } from './ExpenseType.enum';
import { BillTypeEnum } from './BillType.enum';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const styles = () => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
});

const VendorExpenseDetailsForm = (props) => {
    const {
        classes,
        index,
        expense,
        expenseCategories,
        originalExpenseEntries,
        isViewOnly,
        isInvoiceReviewLocked = false,
        mode,
    } = props;
    const { watch, setValue } = useFormContext();
    const expenseCategory = watch(`expenseEntries[${index}].category`);
    const expenseType = watch(`expenseEntries[${index}].type`);
    const billType = watch(`expenseEntries[${index}].billType`);
    const isEndDateRequired =
        expenseCategory === ExpenseCategoryEnum.Travel && expenseType === ExpenseTypeEnum.Rent;
    const isMiscExpense =
        expenseCategory === ExpenseCategoryEnum.Misc && expenseType === ExpenseTypeEnum.Misc;
    const isClinicianDeduction = billType === BillTypeEnum.ClinicianDeduction;
    // Get the original status from the originalExpenseEntries
    const originalStatus = useMemo(() => {
        if (expense.id && originalExpenseEntries?.length > 0) {
            const originalExpense = originalExpenseEntries.find((entry) => entry.id === expense.id);
            return originalExpense ? originalExpense.status : expense.status;
        }
        return expense.status; // Fallback to current status if no original entry is found
    }, [expense.id, originalExpenseEntries, expense.status]);
    // Track the current status being edited
    const currentStatus = watch(`expenseEntries[${index}].status`);
    // Compute isPosted based on original status and current transient state
    const isPosted = useMemo(
        () =>
            originalStatus === ExpenseStatusValuesEnum.Posted ||
            (currentStatus === ExpenseStatusValuesEnum.OnHoldReimbursement &&
                originalStatus === ExpenseStatusValuesEnum.Posted),
        [originalStatus, currentStatus]
    );
    const isApprovedOrInvoiced = [
        ExpenseStatusValuesEnum.Approved,
        ExpenseStatusValuesEnum.Invoiced,
    ].includes(originalStatus);
    const isPendingSubmission = expense.status === ExpenseStatusValuesEnum.PendingSubmission;
    const expenseTypes = useMemo(() => {
        if (!expenseCategory) return [];
        if (!expenseCategories) return [];
        const match = expenseCategories.find((option) => option.category === expenseCategory);
        return match?.types || [];
    }, [expenseCategory, expenseCategories]);

    const isPaymentRequest = expense.expenseClass === ExpenseClassEnum.Payment;
    const isSupplemental = expenseCategory === ExpenseCategoryEnum.Supplemental;
    const endDate = watch(`expenseEntries[${index}].endDate`);

    useEffect(() => {
        if (isSupplemental && endDate) {
            setValue(`expenseEntries[${index}].endDate`, null);
        }
    }, [isSupplemental, endDate, index, setValue]);

    // Clinician Deduction is not available for Supplemental Payment expenses
    useEffect(() => {
        if (isSupplemental && isClinicianDeduction) {
            setValue(`expenseEntries[${index}].billType`, '');
            setValue(`expenseEntries[${index}].bookingBillType`, '');
            setValue(`expenseEntries[${index}].payDate`, null);
        }
    }, [isSupplemental, isClinicianDeduction, index, setValue]);

    return (
        <>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                Expense Details
            </Typography>
            <Grid2 container spacing={3}>
                {Boolean(expense.id) && (
                    <Grid2 item size={{ xs: 12, md: 6 }}>
                        <ExpenseTextField
                            id="expense-id"
                            label="Expense ID"
                            value={String(expense.id)}
                            disabled
                        />
                    </Grid2>
                )}
                <Grid2 item size={{ xs: 12, md: 12 }}>
                    <ExpenseBillType
                        index={index}
                        expense={expense}
                        expenseClass={ExpenseClassEnum.Payment}
                        expenseCategory={expenseCategory}
                        isClinicianReimbursement={!isPaymentRequest}
                        isSGA={[]}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                        mode={mode}
                    />
                </Grid2>
                <Grid2 size={{ md: 12 }}>
                    <ExpenseCategoryType
                        expenseTypes={expenseTypes}
                        index={index}
                        expense={expense}
                        expenseCategories={expenseCategories}
                        isSGA={[]}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                        mode={mode}
                    />
                </Grid2>
                {isClinicianDeduction && (
                    <Grid2 item size={{ md: 12 }}>
                        <ExpensePayDate
                            index={index}
                            expense={expense}
                            mode={ExpenseModeEnum.Payment}
                            isPosted={isPosted}
                            isApprovedOrInvoiced={isApprovedOrInvoiced}
                            isPendingSubmission={isPendingSubmission}
                            isViewOnly={isViewOnly}
                        />
                    </Grid2>
                )}
                <Grid2 item size={{ md: 6 }}>
                    <VendorPaymentStartDate
                        expense={expense}
                        index={index}
                        specialLabel={
                            expenseCategory === ExpenseCategoryEnum.Supplemental && isPaymentRequest
                                ? 'Date'
                                : 'Start Date'
                        }
                        isViewOnly={props?.isViewOnly ?? false}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                    />
                </Grid2>

                {!isSupplemental && (
                    <Grid2 item size={{ md: 6 }}>
                        <VendorPaymentEndDate
                            expense={expense}
                            index={index}
                            isRequired={isEndDateRequired}
                            isViewOnly={props?.isViewOnly ?? false}
                            isInvoiceReviewLocked={isInvoiceReviewLocked}
                        />
                    </Grid2>
                )}

                {isMiscExpense && (
                    <Grid2 size={{ md: 10 }}>
                        <MiscExpenseDescription
                            index={index}
                            expense={expense}
                            isMiscExpense={isMiscExpense}
                            isViewOnly={props?.isViewOnly ?? false}
                            isInvoiceReviewLocked={isInvoiceReviewLocked}
                        />
                    </Grid2>
                )}
                <Grid2 size={{ md: 12 }}>
                    <ExpenseDescription
                        index={index}
                        expense={expense}
                        isViewOnly={props?.isViewOnly ?? false}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                    />
                </Grid2>
            </Grid2>
        </>
    );
};

export default withStyles(styles)(VendorExpenseDetailsForm);
